import {
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';

import { translate } from '@jsverse/transloco';
import { Subject, debounceTime, takeUntil, tap, filter, merge } from 'rxjs';
import { DropdownObject, EnumErrorStates } from '@inmoment/the-kitchen';

import { AppSharedModule } from 'app/app-shared.module';
import { Constants } from 'app/constants/constants';
import {
  FilterService,
  FilterKind,
  WidgetQueryOptions,
} from 'app/modules/collection/services/filter.service';
import { ComparisonFilterSet } from 'app/providers/bridge-api/dashboard.service';
import {
  DocumentService,
  FacetType,
  SearchOptions,
} from 'app/providers/storage-api/document.service';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';
import { ErrorObjectUtils } from 'app/utils/error-object.utils';
import { WidgetDataSource } from '../../../../../models';
import { FilterHandler } from '../comparison-filter-item.component';

@EnableClassTelemetry()
@Component({
  selector: 'app-comparison-filter-keyword-item',
  imports: [AppSharedModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './comparison-filter-keyword-item.component.html',
  styleUrls: ['./comparison-filter-keyword-item.component.scss'],
})
export class ComparisonFilterKeywordItemComponent
  implements FilterHandler<string, string>, OnChanges, OnDestroy
{
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  @Input() dataSource: WidgetDataSource;
  @Input() field: string;
  @Input() filterKind: FilterKind;
  @Input() projectId: string;
  @Input() filters;
  @Input() isEnabled;
  @Input() isReadOnly;

  @Output() filtersChange = new EventEmitter();

  displayValue: string;
  keywords: DropdownObject[];

  isKeywordSelectorVisible;
  search$ = new Subject<string>();
  searchStatus = ErrorObjectUtils.create();
  unsub$ = new Subject<void>();
  filterType: 'contain' | 'not-contain' = 'contain';

  isLoading;
  resultsPage = {
    size: 0,
    current: 0,
    length: 0,
    items: [],
  };
  facetResults: { label; count }[];

  constructor(
    private documentService: DocumentService,
    private filterService: FilterService
  ) {
    this.search$
      .pipe(
        takeUntil(this.unsub$),
        filter((value) => value?.length && value?.length < 3)
      )
      .subscribe(() => {
        this.searchStatus = {
          state: EnumErrorStates.WARNING,
          text: translate('comparison-filter-item.keyword-item.search-minimum-characters'),
        };
      });

    merge(
      this.search$.pipe(filter((value) => !value?.length)),
      this.search$.pipe(
        filter((value) => value?.length > 2),
        debounceTime(400)
      )
    )
      .pipe(
        takeUntil(this.unsub$),
        tap(() => {
          this.isLoading = true;
          this.searchStatus = {
            state: EnumErrorStates.SUCCESS,
          };
        })
      )
      .subscribe(async (value) => {
        await this.loadKeywords(value);

        this.isLoading = false;
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.filters) {
      this.prepareDisplayValue(changes.filters.currentValue);
    }

    if (changes.field || changes.dataSource) {
      this.search$.next('');
    }
  }

  ngOnDestroy(): void {
    this.unsub$.next();
    this.unsub$.complete();
  }

  fromFilter = (filterSet: ComparisonFilterSet) => {
    const operator = this.filterType === 'contain' ? filterSet.and : filterSet.not;

    return operator[0].value;
  };

  toFilter = (value: string) => {
    const filterObj = {
      and: [],
      or: [],
      not: [],
    };

    const operator = this.filterType === 'contain' ? filterObj.and : filterObj.not;
    operator.push({
      analyticItem: this.field,
      value,
      kind: this.filterKind,
    });

    return filterObj;
  };

  hasFilterValue(filterSet: ComparisonFilterSet) {
    return !!filterSet?.and.length || !!filterSet?.not.length;
  }

  prepareDisplayValue(filtersValue) {
    this.keywords = [];
    this.filterType =
      !this.hasFilterValue(filtersValue) || !!filtersValue.and.length ? 'contain' : 'not-contain';

    this.displayValue = this.getItemId(filtersValue);

    if (this.displayValue) {
      this.keywords = [
        {
          id: this.displayValue,
          title: `${
            this.filterType === 'contain'
              ? translate('comparison-filter-item.keyword-item.contains')
              : translate('comparison-filter-item.keyword-item.not-contain')
          } "${this.displayValue}"`,
        },
      ];
    }
  }

  private getItemId(filtersValue) {
    if (this.hasFilterValue(filtersValue)) {
      return this.fromFilter(filtersValue);
    }

    return undefined;
  }

  onFilterValueSet($event) {
    if (!this.isEnabled || this.isReadOnly) {
      return;
    }

    this.displayValue = $event;

    this.isKeywordSelectorVisible = false;

    this.filtersChange.emit(this.toFilter($event));
  }

  openKeywordSelector($event) {
    $event.stopImmediatePropagation();

    if (!this.isEnabled || this.isReadOnly) {
      return;
    }

    this.isKeywordSelectorVisible = true;
  }

  private async loadKeywords(searchText) {
    if (!this.dataSource || !this.field) {
      return;
    }

    const filterOptions = {
      widgetDataSource: this.dataSource,
      analyticItem: this.field,
      type: 'documentCount',
      drilledDownOn: 'none',
      customFieldSelected: true,
      standardFieldSelected: false,
      filters: FilterService.getDefaultFilterSet(),
      sentimentScale: 3,
      selectedItems: [],
    } as WidgetQueryOptions;

    if (this.hasFilterValue(this.filters)) {
      filterOptions.filters.metadata = this.filters;
    }

    const query = this.filterService.getQueryForDocuments(filterOptions, false, true, true);

    const response = await this.searchFacets(searchText, query);
    const responseKey = this.getFacetsResponseKey();

    this.facetResults = [];

    if (response[responseKey]) {
      this.facetResults = Object.keys(response[responseKey])
        .map((k) => ({ label: k, count: response[responseKey][k] }))
        .sort((a, b) => b.count - a.count);
    }

    this.resultsPage = {
      size: 10,
      current: 0,
      length: Object.keys(this.facetResults).length,
      items: [],
    };

    this.showMore();
  }

  private async searchFacets(searchText, query) {
    const keyword = searchText.toLowerCase();
    const searchOptions: SearchOptions = {};

    if (keyword) {
      searchOptions.facetPrefix = `*${keyword}*`;
    }

    searchOptions.facetMetadataName = [this.field];
    searchOptions.facetTypes = [this.getFacetType()];

    if (this.dataSource.type === 'analysis') {
      return this.documentService.getFacetsWithinAnalysis(
        this.dataSource.collectionName,
        this.dataSource.analysisId,
        query,
        undefined,
        undefined,
        searchOptions,
        this.projectId
      );
    } else {
      return this.documentService.getFacetsWithinGroup(
        this.projectId,
        this.dataSource,
        query,
        undefined,
        undefined,
        searchOptions
      );
    }
  }

  getFacetType() {
    // Facet field is a sectionProperty
    if (this.field.startsWith(Constants.SECTION_PROPERTIES_PREFIX)) {
      return this.field as FacetType;
    }

    // Facet field is a sectionMetadata
    if (this.filterKind === 'comparisonSectionFilter') {
      return 'sectionMetadata';
    }

    // Facet field is a simple metadata
    return 'metadata';
  }

  getFacetsResponseKey() {
    // Facet field is a sectionProperty
    if (this.field.startsWith(Constants.SECTION_PROPERTIES_PREFIX)) {
      return `analyses.flatSections.${this.field.replace('sectionProperties.', '')}`;
    }

    // Facet field is a sectionMetadata
    if (this.filterKind === 'comparisonSectionFilter') {
      return `analyses.flatSections.metadata.${this.field}`;
    }

    // Facet field is a simple metadata
    return this.field;
  }

  hasMore() {
    return this.resultsPage.size * this.resultsPage.current < this.resultsPage.length;
  }

  showMore() {
    this.resultsPage.current += 1;

    this.resultsPage.items = this.facetResults.slice(
      0,
      this.resultsPage.size * this.resultsPage.current
    );
  }
}
